import React from 'react';
import { 
  Card, 
  Descriptions, 
  Tag, 
  Space, 
  Statistic, 
  Row, 
  Col, 
  Alert,
  Typography
} from 'antd';
import { 
  ShopOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import type { PlanogramTemplate } from '../../../../types/plan-template';

const { Text } = Typography;

interface ScopeConfigurationProps {
  template: PlanogramTemplate;
}

const ScopeConfiguration: React.FC<ScopeConfigurationProps> = ({
  template
}) => {
  const { t } = useTranslation();
  const scope = template.scopeDefinition;

  // Scope type translation helper
  const getScopeTypeTranslation = (scopeType: string) => {
    const typeKey = scopeType.toLowerCase().replace(' ', '');
    return t(`planTemplate.detail.scopeConfig.scopeTypes.${typeKey}`, scopeType);
  };
  
  // Validation status translation helper
  const getValidationStatusTranslation = (status: string) => {
    const statusKey = status.toLowerCase().replace(' ', '');
    return t(`planTemplate.detail.scopeConfig.validationStatusValues.${statusKey}`, status);
  };

  const getValidationStatusColor = (status: string) => {
    switch (status) {
      case 'Validation Passed': return 'success';
      case 'Validation Failed': return 'error';
      case 'Not Validated': return 'warning';
      default: return 'default';
    }
  };

  return (
    <Card 
      title={
        <Space>
          <ShopOutlined />
          {t('planTemplate.detail.scopeConfig.title')}
        </Space>
      }
    >
      <Row gutter={[24, 16]}>
        <Col span={8}>
          <Card size="small" title={t('planTemplate.detail.scopeConfig.scopeOverview')} style={{ height: '100%' }}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label={t('planTemplate.detail.scopeConfig.scopeType')}>
                <Tag color="purple">{getScopeTypeTranslation(scope.scopeType)}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('planTemplate.detail.scopeConfig.affectedStores')}>
                <Statistic 
                  value={scope.storeCount} 
                  suffix={t('planTemplate.detail.scopeConfig.storesUnit')}
                  valueStyle={{ color: '#1890ff', fontSize: '20px' }}
                />
              </Descriptions.Item>
              <Descriptions.Item label={t('planTemplate.detail.scopeConfig.validationStatus')}>
                <Tag color={getValidationStatusColor(scope.scopeValidationStatus)}>
                  {getValidationStatusTranslation(scope.scopeValidationStatus)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('planTemplate.detail.scopeConfig.lastUpdate')}>
                <Text>{dayjs(scope.lastScopeUpdate).format('YYYY-MM-DD HH:mm')}</Text>
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
        <Col span={16}>
          <Card size="small" title={t('planTemplate.detail.scopeConfig.scopeDetails')} style={{ height: '100%' }}>
            {scope.scopeType === 'All Stores' && (
              <Alert
                message={t('planTemplate.detail.scopeConfig.allStores')}
                description={t('planTemplate.detail.scopeConfig.allStoresDesc')}
                type="info"
                showIcon
              />
            )}
            
            {scope.scopeType === 'Specific Store Groups' && scope.storeGroupList.length > 0 && (
              <div>
                <Text strong>{t('planTemplate.detail.scopeConfig.selectedStoreGroups')}</Text>
                <div style={{ marginTop: 8 }}>
                  <Space wrap>
                    {scope.storeGroupCodeList.map(groupId => (
                      <Tag key={groupId} icon={<ShopOutlined />} color="blue">
                        {groupId}
                      </Tag>
                    ))}
                  </Space>
                </div>
              </div>
            )}
            
            {scope.scopeType === 'Specific Stores' && scope.storeDisplayGroupList.length > 0 && (
              <div>
                <Text strong>{t('planTemplate.detail.scopeConfig.selectedStores')}</Text>
                <div style={{ marginTop: 8 }}>
                  <Space wrap>
                    {scope.storeDisplayGroupList.slice(0, 20).map(storeInfo => (
                      <Tag key={storeInfo.displayGroupCode} icon={<ShopOutlined />} color="green">
                        {storeInfo.storeCode} ( {storeInfo.displayGroupCode} )
                      </Tag>
                    ))}
                    {scope.storeDisplayGroupList.length > 20 && (
                      <Tag color="default">{t('planTemplate.detail.scopeConfig.moreStores', { count: scope.customStoreList.length - 20 })}</Tag>
                    )}
                  </Space>
                </div>
              </div>
            )}
            
            {scope.excludedStores.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <Text strong>{t('planTemplate.detail.scopeConfig.excludedStores')}</Text>
                <div style={{ marginTop: 8 }}>
                  <Space wrap>
                    {scope.excludedStores.slice(0, 10).map(storeId => (
                      <Tag key={storeId} color="red">
                        {storeId}
                      </Tag>
                    ))}
                    {scope.excludedStores.length > 10 && (
                      <Tag color="default">{t('planTemplate.detail.scopeConfig.moreStores', { count: scope.excludedStores.length - 10 })}</Tag>
                    )}
                  </Space>
                </div>
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </Card>
  );
};

export default ScopeConfiguration;